import React from 'react';
import { Badge } from './Badge';
import './TableCard.css';

interface Customer {
  name: string;
  avatarUrl?: string;
}

interface Props {
  number: number;
  status: 'occupied' | 'paying';
  isLong?: boolean;
  covers: number;
  totalTTC: number;
  amountPaid?: number;
  duration: string;
  openedAt?: string;
  owner: string;
  nbItems?: number;
  customer?: Customer | null;
  onClick?: () => void;
}

function fmtOpenedAt(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Card d'une table ouverte. Couleur de bord selon priorité d'urgence :
 *   long (rouge) > paying (orange) > occupied (bleu).
 *
 * Le slot Client est un placeholder permanent (avatar gris + texte italique)
 * jusqu'à ce que le modèle Client soit branché. owner Cashpad masqué tant
 * qu'il vaut "Administrateur" (bruit générique non actionnable en salle).
 */
export function TableCard({
  number,
  status,
  isLong,
  covers,
  totalTTC,
  amountPaid = 0,
  duration,
  openedAt,
  owner,
  nbItems = 0,
  customer = null,
  onClick,
}: Props) {
  const tone = isLong ? 'long' : status;
  const ownerLabel = owner && owner !== 'Administrateur' ? owner : null;
  const opened = fmtOpenedAt(openedAt);
  const remaining = Math.max(0, totalTTC - amountPaid);
  return (
    <button type="button" className={`staff-table-card staff-table-card-${tone}`} onClick={onClick}>
      <div className="staff-table-card-header">
        <span className="staff-table-card-num">Table N°{number}</span>
        <div className="staff-table-card-badges">
          {isLong && <Badge tone="long" size="sm">Long</Badge>}
          {status === 'paying' && !isLong && <Badge tone="paying" size="sm">Paiement</Badge>}
        </div>
      </div>

      <div className="staff-table-card-customer">
        {customer ? (
          customer.avatarUrl ? (
            <img className="staff-table-card-avatar" src={customer.avatarUrl} alt="" />
          ) : (
            <span className="staff-table-card-avatar staff-table-card-avatar-placeholder" aria-hidden="true">
              <UserIcon />
            </span>
          )
        ) : (
          <span className="staff-table-card-avatar staff-table-card-avatar-placeholder" aria-hidden="true">
            <UserIcon />
          </span>
        )}
        <span className={`staff-table-card-customer-name${customer ? '' : ' staff-table-card-customer-empty'}`}>
          {customer ? customer.name : 'Client non identifié'}
        </span>
      </div>

      <div className="staff-table-card-meta">
        {ownerLabel && <span className="staff-table-card-owner">{ownerLabel}</span>}
        <span className="staff-table-card-row">
          {opened && <><span>Ouverte {opened}</span><span>·</span></>}
          <span>{duration}</span>
          {covers > 0 && <><span>·</span><span>{covers} couv.</span></>}
        </span>
        <span className="staff-table-card-row">
          <span>{nbItems} article{nbItems > 1 ? 's' : ''}</span>
        </span>
        {status === 'paying' && amountPaid > 0 && (
          <span className="staff-table-card-row">
            <span>Encaissé {amountPaid.toFixed(2)} €</span>
            <span>·</span>
            <span>reste {remaining.toFixed(2)} €</span>
          </span>
        )}
      </div>

      <div className="staff-table-card-total">{totalTTC.toFixed(2)} €</div>
    </button>
  );
}

function UserIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}
